import Vue from "vue";
import { IFullPartDetailWithReplacementItemsDto } from "@/types";
import { mapGetters } from "vuex";
import {
  formatDateTime,
  stringHelper,
  sizeHelper,
} from "@/components/mixins";
import { ShoppingCartService } from "@/services/ShoppingCartService";
import {
  LoadingSpinner,
  DnFunctionButton
} from "@/components/Elements";
import CurrencyText from "@/components/Elements/CurrencyText.vue";
import SkeletonLoader from "@/components/Elements/SkeletonLoader.vue";
import StockInformationPopover from "@/components/Elements/StockInformationPopover";
import { PartValidationReason } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";

export default Vue.extend({
  name: "ReplacementItems",
  components: {
    LoadingSpinner,
    DnFunctionButton,
    CurrencyText,
    StockInformationPopover,
    FontAwesomeIcon,
    SkeletonLoader,
  },
  mixins: [formatDateTime, stringHelper, sizeHelper],
  props: {
    replacementItems: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    itemLoadingStates: [] as boolean[],
    itemQuantities: [] as number[],
    addingAllToCart: false,
    arrowDownIcon: faArrowDown,
  }),
  computed: {
    ...mapGetters([
      "user",
      "activeAccountId",
      "cartSummary",
      "hasPermission",
      "isPriceVisible",
      "IsMcfa",
    ]),
    totalPrice(): number {
      return this.replacementItems.reduce((sum: number, item: any, index: number) => {
        const price = this.getItemPrice(item);
        const qty = this.itemQuantities[index] || this.getItemQuantity(item);
        return sum + (price || 0) * qty;
      }, 0);
    },
  },
  watch: {
    replacementItems: {
      handler(items: any[]) {
        this.itemLoadingStates = items.map(() => false);
        this.itemQuantities = items.map((item: any) => this.getItemQuantity(item));
      },
    },
  },
  methods: {
    showItemPrice(item: any): boolean {
      if (this.IsNullOrUndefined(item, "FullPartDetails")) return false;
      // Must have a price
      if (!(item.FullPartDetails.DealerNetPrice > 0 || item.FullPartDetails.ContractPrice > 0)) return false;
      // Permission check
      if (!this.allowedToSeePartPrices(item)) return false;
      // Validation reason check
      if (!this.showPrices(item)) return false;
      return true;
    },
    allowedToSeePartPrices(item: any): boolean {
      if (this.IsNullOrUndefined(item, "FullPartDetails")) 
        {
          return false;
        }
      if (item.FullPartDetails.DealerNetPrice > 0 && this.hasPermission("CanSeeDealerNet")) 
        {
          return true;
        }
      if (item.FullPartDetails.ContractPrice > 0 && this.hasPermission("CanSeeContractPrice")) 
        {
          return true;
        }
      return false;
    },
    showPrices(item: any): boolean {
      if (!this.isPriceVisible) return false;
      const partValidationReason = item.FullPartDetails.Validation?.ValidationReason;
      if (partValidationReason === undefined || partValidationReason === null) return true;
      // Hide pricing on soft-frozen controller parts for MCFA
      if (this.IsMcfa &&
        partValidationReason === PartValidationReason.IsSoftFrozen &&
        item.FullPartDetails.PicCode === "608") {
        return false;
      }
      const invalidReasons = [
        PartValidationReason.IsChannelNotAuthorized,
        PartValidationReason.IsCanceled,
        PartValidationReason.IsSuperseded,
        PartValidationReason.IsDiscontinued,
        PartValidationReason.ItemNotPriced,
        PartValidationReason.IsMaoNoPrice,
        PartValidationReason.IsProhibited,
        PartValidationReason.IsHardFrozen,
        PartValidationReason.IsMaoLimitExceededPriceNotCurrentNoStockQuantity,
        PartValidationReason.IsMaoLimitExceededPriceNotCurrent,
        PartValidationReason.MaoSoftFrozen,
      ];
      if (invalidReasons.includes(partValidationReason)) return false;
      return true;
    },
    getItemPrice(item: any): number {
      if (this.IsNullOrUndefined(item, "FullPartDetails")) return 0;
      const contract = item.FullPartDetails.ContractPrice;
      const dealerNet = item.FullPartDetails.DealerNetPrice;
      if (contract && contract !== dealerNet && contract !== 0) {
        return contract;
      }
      return dealerNet || 0;
    },
    isItemOrderable(item: any): boolean {
      if (!this.hasPermission("CanPlaceOrders")) return false;
      if (this.IsNullOrUndefined(item, "FullPartDetails.PartStatusMessage.IsOrderable")) return false;
      return item.FullPartDetails.PartStatusMessage.IsOrderable === true;
    },
    getItemQuantity(item: any): number {
      return item.Quantity && item.Quantity > 0 ? item.Quantity : 1;
    },
    addItemToCart(item: any) {
      const index = this.replacementItems.indexOf(item);
      if (index >= 0) {
        this.$set(this.itemLoadingStates, index, true);
      }

      const qty = index >= 0 ? (this.itemQuantities[index] || 1) : this.getItemQuantity(item);

      const params = {
        shoppingCartId: this.cartSummary.Id,
        partId: item.FullPartDetails.Id,
        quantity: qty,
        accountId: this.activeAccountId,
        userId: this.user.UserId,
      };

      ShoppingCartService.AddShoppingCartItem(params)
        .then(() => {
          this.$store.dispatch("LOAD_CART_SUMMARY", {
            accountId: this.activeAccountId,
            userId: this.user.UserId,
          });
          this.$store.commit("pushTimedMessage", {
            header: `Item ${item.FullPartDetails.OemPartCode} added to cart successfully`,
            IsError: false,
            seconds: 5,
          });
        })
        .catch(() => {
          this.$store.commit("pushTimedMessage", {
            header: `Error adding ${item.FullPartDetails.OemPartCode} to cart`,
            IsError: true,
            seconds: 5,
          });
        })
        .finally(() => {
          if (index >= 0) {
            this.$set(this.itemLoadingStates, index, false);
          }
        });
    },
    addAllToCart() {
      this.addingAllToCart = true;

      const partOemPartCodesAndQuantities: Record<string, number> = {};
      this.replacementItems.forEach((item: IFullPartDetailWithReplacementItemsDto, index: number) => {
        if (this.isItemOrderable(item)) {
          partOemPartCodesAndQuantities[item.FullPartDetails.OemPartCode] = this.itemQuantities[index] || this.getItemQuantity(item);
        }
      });

      const request = {
        shoppingCartId: this.cartSummary.Id,
        accountId: this.activeAccountId,
        userId: this.user.UserId,
        partOemPartCodesAndQuantities,
      };

      ShoppingCartService.AddShoppingCartItems(request)
        .then(() => {
          this.$store.dispatch("LOAD_CART_SUMMARY", {
            accountId: this.activeAccountId,
            userId: this.user.UserId,
          });
          this.$store.commit("pushTimedMessage", {
            header: "All replacement items added to cart successfully",
            IsError: false,
            seconds: 5,
          });
        })
        .catch(() => {
          this.$store.commit("pushTimedMessage", {
            header: "Error adding replacement items to cart",
            IsError: true,
            seconds: 5,
          });
        })
        .finally(() => {
          this.addingAllToCart = false;
        });
    },
  },
});
